import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Check, Plus } from "lucide-react";

const CURATED_CATEGORIES = ["Machinery", "Furniture & Fixtures"];

export function isCuratedCategory(cat) {
  return CURATED_CATEGORIES.includes(cat);
}

/**
 * Reusable Name field:
 *  - Curated dropdown for Machinery / Furniture & Fixtures (shared across app)
 *  - Falls back to a plain text input for other categories
 *  - "+ Add new" opens a suggestion-aware dialog that helps users pick an
 *    existing name (avoids caps/typo duplicates) before creating a new entry.
 *
 * Props:
 *   category, value, onChange(nextName), required, testid
 */
export default function AssetNameSelect({
  category,
  value,
  onChange,
  required = false,
  testid = "asset-name",
  placeholder = "",
}) {
  const { user } = useAuth();
  const [options, setOptions] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const canManage = (user?.effective_capabilities || []).includes("manage_asset_name_options");
  const curated = isCuratedCategory(category);

  const load = useCallback(async () => {
    if (!curated) { setOptions([]); return; }
    try {
      const { data } = await api.get("/asset-name-options", { params: { category } });
      setOptions(data || []);
    } catch { setOptions([]); }
  }, [category, curated]);

  useEffect(() => { load(); }, [load]);

  const onAdded = (opt) => {
    setOptions((opts) => [...opts, opt].sort((a, b) => a.name.localeCompare(b.name)));
    onChange?.(opt.name);
    toast.success(`Added "${opt.name}" to the ${category} dropdown`);
  };

  const onPickExisting = (name) => {
    onChange?.(name);
    toast.success(`Selected existing name "${name}" — no duplicate created`);
  };

  if (!curated) {
    return (
      <Input
        required={required}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={`${testid}-input`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row gap-2">
        <Select value={value || ""} onValueChange={(v) => onChange?.(v)}>
          <SelectTrigger className="flex-1" data-testid={`${testid}-select`}>
            <SelectValue placeholder={placeholder || `Select ${category.toLowerCase()}...`} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {options.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-500">
                No {category} options yet.{canManage ? " Click 'Add new' to create one." : " Ask an admin to add options."}
              </div>
            )}
            {options.map((o) => (
              <SelectItem key={o.id} value={o.name}>
                <span className="inline-flex items-center gap-2 w-full">
                  <span>{o.name}</span>
                  {o.usage_count > 0 && (
                    <span className="ml-auto text-[10px] font-mono text-slate-500 border border-slate-200 rounded px-1.5 py-0.5 bg-slate-50">
                      {o.usage_count} in use
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canManage && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setAddOpen(true)}
            className="whitespace-nowrap"
            data-testid={`${testid}-add-btn`}
          >
            <Plus className="w-4 h-4 mr-1" /> Add new
          </Button>
        )}
      </div>

      {addOpen && (
        <AddAssetNameDialog
          category={category}
          existing={options}
          onClose={() => setAddOpen(false)}
          onAdded={(opt) => { setAddOpen(false); onAdded(opt); }}
          onPickExisting={(name) => { setAddOpen(false); onPickExisting(name); }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Add-new dialog with live suggestions so users don't accidentally create
// near-duplicates (caps/spacing/typo variants).
// ---------------------------------------------------------------------------
function AddAssetNameDialog({ category, existing, onClose, onAdded, onPickExisting }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();

  // Detect exact case-insensitive match (would be rejected by the API)
  const exactMatch = useMemo(() => {
    if (!trimmed) return null;
    return existing.find(
      (o) => o.name.trim().toLowerCase() === trimmed.toLowerCase() && o.active !== false,
    );
  }, [existing, trimmed]);

  // Score suggestions:
  //   - contains (case-insensitive) → strong hit
  //   - starts-with → higher priority
  //   - Levenshtein distance ≤ 2 (for short typos) → medium hit
  //   - shares 3+ word tokens → weak hit
  const suggestions = useMemo(() => {
    if (!trimmed || trimmed.length < 2) return [];
    const q = trimmed.toLowerCase();
    const qTokens = q.split(/\s+/).filter((t) => t.length >= 3);
    const scored = existing.map((o) => {
      const n = (o.name || "").toLowerCase();
      let score = 0;
      let reason = null;
      if (n === q) { score = 1000; reason = "exact-match"; }
      else if (n.startsWith(q)) { score = 500; reason = "starts-with"; }
      else if (n.includes(q)) { score = 300; reason = "contains"; }
      // Levenshtein for short typo detection (limit for perf)
      const lev = levenshtein(n, q);
      if (lev <= 2 && q.length >= 4) {
        score = Math.max(score, 200 - lev * 40);
        reason = reason || (lev === 0 ? "exact-match" : lev === 1 ? "one-char" : "typo");
      }
      const tokenMatches = qTokens.filter((t) => n.includes(t)).length;
      if (tokenMatches >= 2) {
        score = Math.max(score, 100 + tokenMatches * 20);
        reason = reason || "token-overlap";
      } else if (tokenMatches === 1 && score === 0 && q.length >= 5) {
        score = 40;
        reason = "token-overlap";
      }
      return { ...o, __score: score, __reason: reason, __lev: lev };
    });
    return scored
      .filter((o) => o.__score > 0 && o.active !== false)
      .sort((a, b) => b.__score - a.__score)
      .slice(0, 8);
  }, [existing, trimmed]);

  const hasClose = suggestions.some((s) => s.__reason === "one-char" || s.__reason === "typo");

  const add = async () => {
    if (!trimmed || exactMatch) return;
    setBusy(true);
    try {
      const { data } = await api.post("/asset-name-options", { category, name: trimmed });
      onAdded(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add option");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="add-asset-name-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a new {category} name</DialogTitle>
          <DialogDescription className="text-xs">
            Start typing — the app will surface existing names that look similar so you don't accidentally
            create duplicates from caps / spacing / typo differences.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`e.g. Juki DDL-8100e single-needle sewing machine`}
              data-testid="add-asset-name-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !exactMatch && trimmed) { e.preventDefault(); add(); } }}
            />
            {trimmed.length > 0 && trimmed.length < 3 && (
              <p className="text-[11px] text-slate-500 mt-1">Type at least 3 characters to see suggestions.</p>
            )}
          </div>

          {exactMatch && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
              data-testid="add-asset-name-exact-warning"
            >
              <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900">
                  This name already exists (case-insensitive match)
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Existing entry: <span className="font-mono">"{exactMatch.name}"</span>
                  {exactMatch.usage_count > 0 && <> · used by {exactMatch.usage_count} asset{exactMatch.usage_count !== 1 ? "s" : ""}</>}
                </div>
                <Button
                  size="sm"
                  className="mt-2"
                  variant="outline"
                  onClick={() => onPickExisting(exactMatch.name)}
                  data-testid="add-asset-name-pick-exact"
                >
                  <Check className="w-3 h-3 mr-1" /> Use existing
                </Button>
              </div>
            </div>
          )}

          {!exactMatch && hasClose && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              A very similar name already exists — please review the suggestions below before adding.
            </div>
          )}

          {suggestions.length > 0 && !exactMatch && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Existing similar names — click to use one instead
              </div>
              <div className="border rounded-md divide-y" data-testid="add-asset-name-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onPickExisting(s.name)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                    data-testid={`add-asset-name-suggestion-${s.id}`}
                  >
                    <span className="text-sm text-slate-900 flex-1">{s.name}</span>
                    {s.__reason === "one-char" && (
                      <span className="text-[9px] font-mono uppercase bg-red-100 text-red-800 border border-red-200 rounded px-1.5 py-0.5">1 char off</span>
                    )}
                    {s.__reason === "typo" && (
                      <span className="text-[9px] font-mono uppercase bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">likely typo</span>
                    )}
                    {s.__reason === "starts-with" && (
                      <span className="text-[9px] font-mono uppercase bg-blue-100 text-blue-800 border border-blue-200 rounded px-1.5 py-0.5">starts with</span>
                    )}
                    {s.__reason === "contains" && (
                      <span className="text-[9px] font-mono uppercase bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5">contains</span>
                    )}
                    {s.__reason === "token-overlap" && (
                      <span className="text-[9px] font-mono uppercase bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5">similar words</span>
                    )}
                    {s.usage_count > 0 && (
                      <span className="text-[10px] font-mono text-slate-500">{s.usage_count} in use</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {trimmed.length >= 3 && suggestions.length === 0 && !exactMatch && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900 flex items-center gap-2">
              <Check className="w-3 h-3 shrink-0" />
              No similar names found — safe to add as a brand-new entry.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-asset-name-cancel">Cancel</Button>
          <Button
            onClick={add}
            disabled={!trimmed || !!exactMatch || busy}
            data-testid="add-asset-name-submit"
          >
            <Plus className="w-4 h-4 mr-1" />
            {busy ? "Adding…" : "Add new name"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Small Levenshtein distance function — enough for typo detection on short strings
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  // Bail out early for very different lengths (prevents heavy DP for unrelated names)
  if (Math.abs(a.length - b.length) > 6) return 99;
  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,       // deletion
        curr[j - 1] + 1,   // insertion
        prev[j - 1] + cost // substitution
      );
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}
